<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <!-- Customer default week: dagvinna (+ planned yfirvinna with a custom overtime schedule) per weekday -->
      <div class="col-12">
        <card :title="$t('workSchedules.companyDefaultTitle')">
          <p class="text-sm text-secondary">{{ $t('workSchedules.companyDefaultHint') }}</p>
          <div v-if="defaultWeek" class="table-responsive">
            <table class="table table-sm align-middle mb-2 week-table">
              <thead>
                <tr>
                  <th></th>
                  <th v-for="(day, i) in dayFields" :key="day" class="text-center text-sm">{{ weekdayLabel(i) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="text-xs fw-bold text-uppercase text-secondary">{{ $t('endOfDay.normalHours') }}</td>
                  <td v-for="day in dayFields" :key="day"><input v-model="defaultWeek[day]" type="number" step="0.25" min="0" max="24" class="form-control form-control-sm text-center" /></td>
                </tr>
                <tr v-if="customOvertime">
                  <td class="text-xs fw-bold text-uppercase text-warning">{{ $t('endOfDay.overtimeHours') }}</td>
                  <td v-for="day in dayFields" :key="day"><input v-model="defaultWeek[overtimeField(day)]" type="number" step="0.25" min="0" max="24" class="form-control form-control-sm text-center overtime-input" /></td>
                </tr>
                <tr v-if="customOvertime">
                  <td class="text-xs fw-bold text-uppercase text-secondary">{{ $t('timeRegistration.total') }}</td>
                  <td v-for="day in dayFields" :key="day" class="text-center fw-bold">{{ formatHours(dayTotal(defaultWeek, day)) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="d-flex">
              <el-button type="primary" class="ms-auto" :loading="savingDefault" @click="saveDefault">{{ $t('common.save') }}</el-button>
            </div>
          </div>
        </card>
      </div>

      <div class="col-12">
        <card :title="$t('workSchedules.title')">
          <p class="text-sm text-secondary">{{ $t('workSchedules.hint') }}</p>
          <div v-if="!loaded" class="text-center text-secondary py-4">{{ $t('common.loading') }}</div>
          <el-table v-else :data="schedules" row-key="userId" style="width:100%">
            <el-table-column :label="$t('timeRegistration.employee')" prop="userName" min-width="160" />
            <el-table-column v-for="(day, i) in dayFields" :key="day" :label="weekdayLabel(i)" width="95" align="center">
              <template #default="scope">
                <div :class="{ 'deviates': deviatesFromDefault(scope.row, day) }">
                  <template v-if="customOvertime">
                    <div class="fw-semibold">{{ formatHours(dayTotal(scope.row, day)) }}</div>
                    <div v-if="dayTotal(scope.row, day) > 0" class="text-xs text-secondary">{{ formatHours(scope.row[day]) }} + <span class="text-warning">{{ formatHours(scope.row[overtimeField(day)]) }}</span></div>
                  </template>
                  <template v-else>{{ formatHours(scope.row[day]) }}</template>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.status')" width="120">
              <template #default="scope">
                <el-tag v-if="scope.row.isDefault" type="info" size="small">{{ $t('workSchedules.defaultSchedule') }}</el-tag>
                <el-tag v-else type="success" size="small">{{ $t('workSchedules.customSchedule') }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="" width="80" align="right">
              <template #default="scope">
                <el-button size="small" @click="openEdit(scope.row)">
                  <i class="material-icons" style="font-size:1rem">edit</i>
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div v-if="totalPages > 1" class="d-flex align-items-center gap-2 mt-3">
            <el-button size="small" :disabled="currentPage <= 1" @click="changePage(-1)">
              <i class="material-icons" style="font-size:1rem">chevron_left</i>
            </el-button>
            <span class="text-sm text-secondary">{{ currentPage }} / {{ totalPages }}</span>
            <el-button size="small" :disabled="currentPage >= totalPages" @click="changePage(1)">
              <i class="material-icons" style="font-size:1rem">chevron_right</i>
            </el-button>
          </div>
        </card>
      </div>
    </div>

    <el-dialog v-model="showForm" :title="$t('workSchedules.editSchedule')" :width="customOvertime ? 560 : 480">
      <p v-if="form.userName" class="text-sm text-secondary mb-3">{{ form.userName }}</p>
      <div v-if="customOvertime" class="d-flex align-items-center gap-2 mb-1 text-xs fw-bold text-uppercase">
        <span style="width:140px"></span>
        <span class="text-secondary" style="width:120px">{{ $t('endOfDay.normalHours') }}</span>
        <span class="text-warning" style="width:120px">{{ $t('endOfDay.overtimeHours') }}</span>
      </div>
      <div v-for="(day, i) in dayFields" :key="day" class="d-flex align-items-center gap-2 mb-2">
        <label class="form-label mb-0" style="width:140px">{{ weekdayLabel(i) }}</label>
        <el-input v-model="form[day]" type="number" step="0.25" min="0" max="24" style="width:120px" />
        <el-input v-if="customOvertime" v-model="form[overtimeField(day)]" type="number" step="0.25" min="0" max="24" style="width:120px" />
        <span v-else class="text-secondary text-sm">{{ $t('timeRegistration.hrs') }}</span>
      </div>
      <template #footer>
        <el-button v-if="!form.isDefault" class="float-start" :loading="saving" @click="resetToDefault">{{ $t('workSchedules.resetToDefault') }}</el-button>
        <el-button @click="showForm = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">{{ $t('common.submit') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../services/api';
import Card from '../../components/Card.vue';
import { ElDialog, ElInput, ElButton, ElTable, ElTableColumn, ElTag } from 'element-plus';
import { getCustomerConfig } from '../Settings/customerConfigHelper.js';
import { weekdayShort } from '../../utils/dateNames.js';

/**
 * ManageWorkSchedules — Admin view of expected work hours per weekday per user.
 * Users with no schedule row inherit the customer default week ("Full vinnuvika", edited here;
 * 8h Mon–Fri when none is set). With a custom overtime schedule (EnableCustomOvertimeSchedule)
 * each day also has planned yfirvinna.
 */
export default {
  name: 'ManageWorkSchedules',
  components: {
    Card,
    ElDialog, ElInput, ElButton, ElTable, ElTableColumn, ElTag,
  },
  data() {
    return {
      loaded: false,
      schedules: [],
      currentPage: 1,
      totalPages: 1,
      pageSize: 50,
      showForm: false,
      saving: false,
      form: { userId: null, userName: '', isDefault: true },
      dayFields: ['mondayHours', 'tuesdayHours', 'wednesdayHours', 'thursdayHours', 'fridayHours', 'saturdayHours', 'sundayHours'],
      customOvertime: false,
      defaultWeek: null,
      savingDefault: false,
    };
  },
  async created() {
    this.customOvertime = (await getCustomerConfig('EnableCustomOvertimeSchedule')) === true;
    await this.fetchDefault();
    this.fetchSchedules();
  },
  methods: {
    // Columns start on Monday; 2024-01-01 was a Monday.
    weekdayLabel(i) {
      return weekdayShort(this.$t, new Date(2024, 0, 1 + i));
    },
    formatHours(h) {
      const v = Number(h);
      return Number.isFinite(v) ? v.toFixed(2) : '0.00';
    },
    overtimeField(day) { return day.replace('Hours', 'OvertimeHours'); },
    dayTotal(week, day) { return (Number(week[day]) || 0) + (Number(week[this.overtimeField(day)]) || 0); },
    deviatesFromDefault(row, day) {
      if (!this.defaultWeek || row.isDefault) return false;
      if (Number(row[day]) !== Number(this.defaultWeek[day])) return true;
      const od = this.overtimeField(day);
      return this.customOvertime && Number(row[od]) !== Number(this.defaultWeek[od]);
    },
    /**
     * Reads the 14 hour fields from a form/week object; null when any value is invalid. Planned
     * yfirvinna is sent even while it's hidden (custom overtime off) so saving doesn't wipe it.
     */
    weekBody(source) {
      const body = {};
      for (const day of this.dayFields) {
        for (const f of [day, this.overtimeField(day)]) {
          const v = Number(source[f] ?? 0);
          if (!Number.isFinite(v) || v < 0 || v > 24) return null;
          body[f] = v;
        }
        if (body[day] + body[this.overtimeField(day)] > 24) return null;
      }
      return body;
    },
    async fetchDefault() {
      try {
        const res = await api.get('/WorkSchedule/default');
        this.defaultWeek = res.data;
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    async saveDefault() {
      const body = this.weekBody(this.defaultWeek);
      if (!body) {
        this.$toast.danger(this.$t('workSchedules.invalidHours'), { timeout: 3000 });
        return;
      }
      this.savingDefault = true;
      try {
        const res = await api.put('/WorkSchedule/default', body);
        this.defaultWeek = res.data;
        await this.fetchSchedules();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$toast.danger(err?.response?.data || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.savingDefault = false;
      }
    },
    async fetchSchedules() {
      this.loaded = false;
      try {
        const res = await api.get(`/WorkSchedule?currentPage=${this.currentPage}&pageSize=${this.pageSize}`);
        this.schedules = res.data.result || [];
        this.totalPages = res.data.totalPages || 1;
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      } finally {
        this.loaded = true;
      }
    },
    changePage(delta) {
      this.currentPage += delta;
      this.fetchSchedules();
    },
    openEdit(row) {
      this.form = { userId: row.userId, userName: row.userName, isDefault: row.isDefault };
      for (const day of this.dayFields) {
        this.form[day] = Number(row[day]);
        this.form[this.overtimeField(day)] = Number(row[this.overtimeField(day)]) || 0;
      }
      this.showForm = true;
    },
    async submitForm() {
      const body = this.weekBody(this.form);
      if (!body) {
        this.$toast.danger(this.$t('workSchedules.invalidHours'), { timeout: 3000 });
        return;
      }
      body.userId = this.form.userId;
      this.saving = true;
      try {
        await api.put('/WorkSchedule', body);
        this.showForm = false;
        await this.fetchSchedules();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$toast.danger(err?.response?.data || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.saving = false;
      }
    },
    async resetToDefault() {
      this.saving = true;
      try {
        await api.delete(`/WorkSchedule/${this.form.userId}`);
        this.showForm = false;
        await this.fetchSchedules();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$toast.danger(err?.response?.data || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.week-table input { min-width: 64px; }
.overtime-input { background: #fff8e6; border-color: #f5d98b; }
.deviates { background: #fff1f0; border-radius: 4px; padding: 2px 0; }
</style>
